import { useRef, useState } from "react";
import { Box } from "@mui/material";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";

import type {
  ExtendedThalamosUser,
  FormContextData,
} from "@thalamos/aspire-common";
import {
  isCqcUserSession,
  isFormContextTypeEnabledForOrg,
  isGuestUserSession,
  isSoadUserSession,
} from "@thalamos/aspire-common";

import { api } from "../../../api.js";
import { routeFns } from "../../../routePaths.js";
import {
  hasNoActiveWorkItemOrRejectedOrCancelled,
  isAmendForOtherUser,
} from "./workItemHelpers.js";
import { Banner, BannerList } from "~/components/design-system/index.js";
import { ConfirmationModal } from "~/pages/ConfirmationModal.js";

export function WorkItemStatusCard({
  formContext,
  reloadFormContext,
  user,
  amAmhpUser,
  suppressTeamClaimBanner,
}: {
  formContext: FormContextData;
  reloadFormContext: () => void;
  user: ExtendedThalamosUser;
  amAmhpUser?: boolean;
  suppressTeamClaimBanner?: boolean;
}) {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showConfirmClaim, setShowConfirmClaim] = useState(false);
  const [claimSucceeded, setClaimSucceeded] = useState(false);
  // Synchronous re-entrancy guard: prevents a second confirmFn invocation
  // before React has had a chance to re-render and disable the button.
  const isClaimingRef = useRef(false);

  const isFormContextClosed = formContext.status === "closed";

  const isWorkItemCompleted =
    formContext.activeTeamworkWorkItem?.status === "completed";

  // You don't have a work item
  if (hasNoActiveWorkItemOrRejectedOrCancelled(formContext)) {
    // Optimistically hide the banner immediately after a successful claim,
    // without waiting for the parent to finish reloading the form context.
    if (claimSucceeded) return <></>;

    const isFormTypeEnabled = user.sessionOrganisationConfiguration
      ? isFormContextTypeEnabledForOrg(
          formContext.type,
          formContext.forms,
          user.sessionOrganisationConfiguration,
        )
      : false;

    if (!isFormTypeEnabled) {
      return (
        <Box sx={{ mb: 2 }}>
          <Banner
            bannerType={BannerList.WARNING}
            title={
              "You are viewing this assessment as read-only because you are not a participant. If you feel you should be involved in this assessment please contact one of the participants listed below outside of eMHA and ask them to invite you"
            }
          />
        </Box>
      );
    }

    return (
      <Box sx={{ mb: 2 }}>
        {showConfirmClaim && (
          <ConfirmationModal
            confirmFn={() => {
              // Synchronous guard: if already in-flight, ignore this click.
              if (isClaimingRef.current) return;
              isClaimingRef.current = true;
              setShowConfirmClaim(false);
              setIsSubmitting(true);
              const run = async () => {
                try {
                  await api.forms.giveMyselfAccess(formContext.id);
                  setClaimSucceeded(true);
                  reloadFormContext();
                } finally {
                  isClaimingRef.current = false;
                  setIsSubmitting(false);
                }
              };
              void run();
            }}
            disabledConfirm={isSubmitting}
            closeFn={() => {
              setShowConfirmClaim(false);
            }}
            message={
              "By claiming this assessment, you will become a participant. The assessment will appear in your Team Caseload and be viewable by members within your team."
            }
          ></ConfirmationModal>
        )}
        <Banner
          bannerType={BannerList.WARNING}
          title={
            "You're viewing this assessment in read-only mode because you're not currently a participant. If you believe you should be involved in this assessment please click the 'Claim' button."
          }
          rightButtonLabel={"Claim"}
          disabledButtonRight={isSubmitting}
          onRightButton={() => {
            setShowConfirmClaim(true);
          }}
        />
      </Box>
    );
  }

  // Is the workitem completed or is the workItem completed and the form context is closed
  if (isWorkItemCompleted) {
    const isFinalised = formContext.forms?.[0]?.status === "finalised";

    const canUnfinaliseAdmission =
      formContext.activeTeamworkWorkItem?.actions?.some(
        (action) => action.type === "unfinalise",
      );

    const isGuest = isGuestUserSession(user);

    const isGuestUserAndRequestTypeApplication =
      isGuest &&
      formContext.activeTeamworkWorkItem?.requestType === "application";

    const isFormContextTypeAdmission = formContext.type === "admission";

    const isCqc = isCqcUserSession(user);
    const isSoad = isSoadUserSession(user);
    const isSoadOrCqc = isSoad || isCqc;

    const isClosed = formContext.status === "closed";
    const isClosedByUserId = formContext.closedByUserId === user.id;
    const isClosedByTeamId =
      formContext.closedByTeamId === user.sessionContext?.teamId;

    function getBannerTitle(isFinalised: boolean) {
      if (canUnfinaliseAdmission) {
        return "This form is in your team's archive. Legal scrutiny has been marked as complete by an MHA office. No further changes can be made but the form can still be viewed and downloaded. Select UNDO to reverse this action.";
      } else if (isFinalised) {
        return "This form is in your team's archive. Administrative scrutiny has been marked as complete by an MHA office. No further changes can be made but the form can still be viewed and downloaded.";
      } else {
        if (isFormContextTypeAdmission && isWorkItemCompleted && isClosed) {
          return amAmhpUser && (isClosedByUserId || isClosedByTeamId)
            ? t("pages.formPage.bannerInfoArchiveAdmissionNoApp")
            : isFormContextClosed && (!isClosedByUserId || !isClosedByTeamId)
              ? t("pages.formPage.bannerInfoArchiveAdmissionNoAppNonAmhp")
              : t("pages.formPage.bannerInfoArchiveAdmission");
        }
        if (isWorkItemCompleted) {
          return t("pages.formPage.bannerInfoArchive");
        }
      }
    }

    const canReopen =
      amAmhpUser &&
      isClosed &&
      (isClosedByUserId || isClosedByTeamId) &&
      !isFinalised;
    const canUnarchive = !canReopen;

    const rightButtonLabel = canUnfinaliseAdmission
      ? "UNDO"
      : canReopen
        ? "REOPEN"
        : canUnarchive && formContext.status === "active"
          ? "UNARCHIVE"
          : "";

    const isItemCompletedOrFinalised = isFinalised || isWorkItemCompleted;

    return (
      <>
        {isItemCompletedOrFinalised && !isSoadOrCqc && (
          <Box sx={{ mb: 2 }}>
            <Banner
              bannerType={BannerList.INFO}
              title={getBannerTitle(isFinalised) || ""}
              rightButtonLabel={rightButtonLabel}
              disabledButtonRight={isSubmitting}
              onRightButton={() => {
                const onSubmit = async () => {
                  if (canUnfinaliseAdmission) {
                    navigate(
                      routeFns.formContextUnfinaliseDialog(
                        formContext.id,
                        formContext.patientId,
                      ),
                    );
                  } else if (
                    isClosed &&
                    ((amAmhpUser &&
                      isFormContextTypeAdmission &&
                      !isFinalised) ||
                      (isFormContextTypeAdmission &&
                        isGuestUserAndRequestTypeApplication))
                  ) {
                    navigate(
                      routeFns.formContextReopenAdmissionDialog(
                        formContext.id,
                        formContext.patientId,
                      ),
                    );
                  } else {
                    await api.work.unArchive(
                      formContext.activeTeamworkWorkItem!.id,
                    );
                  }
                  setIsSubmitting(true);
                  reloadFormContext();
                  setIsSubmitting(false);
                };
                void onSubmit();
              }}
            />
          </Box>
        )}
      </>
    );
  }

  // A pending amend is assigned to another teammate — show a non-actionable
  // banner. The standard claim banner is suppressed because the claim endpoint
  // rejects pending+already-assigned work items, so the Claim button would
  // always error.
  if (isAmendForOtherUser(formContext.activeTeamworkWorkItem, user.id)) {
    const amendAssignedDoctorName =
      formContext.activeTeamworkWorkItem?.assignedUser?.name ??
      "another doctor";
    return (
      <Box sx={{ mb: 2 }}>
        <Banner
          bannerType={BannerList.WARNING}
          title={`${amendAssignedDoctorName} has been asked to amend part 1 of this medical recommendation. Part 2 can be amended once ${amendAssignedDoctorName} has completed their amend.`}
        />
      </Box>
    );
  }

  // The work item for your team exists but is not claimed by you
  if (
    !suppressTeamClaimBanner &&
    formContext.activeTeamworkWorkItem &&
    formContext.activeTeamworkWorkItem.assignedUserId !== user.id
  ) {
    return (
      <Box sx={{ mb: 2 }}>
        <Banner
          bannerType={BannerList.WARNING}
          title={
            formContext.activeTeamworkWorkItem.assignedUserId
              ? `You can’t work on this assessment as someone else (${
                  formContext.activeTeamworkWorkItem.assignedUser!.name
                }) in your team has claimed it. Click claim if you need to work on this assessment.`
              : `You can’t work on this assessment as it is currently unclaimed in your team. Click claim if you need to work on this assessment.`
          }
          rightButtonLabel={"Claim"}
          disabledButtonRight={isSubmitting}
          onRightButton={() => {
            const onRightButton = async () => {
              setIsSubmitting(true);
              if (formContext.activeTeamworkWorkItem?.assignedUserId) {
                await api.work.unClaim(formContext.activeTeamworkWorkItem!.id);
              }
              await api.work.claim(formContext.activeTeamworkWorkItem!.id);
              reloadFormContext();
              setIsSubmitting(false);
            };
            void onRightButton();
          }}
        />
      </Box>
    );
  }

  // All is ok
  return <></>;
}
